import { get as getCookie, set as setCookie } from 'js/lib/cookie';
import { get as getUser, isAuthenticatedUser, isCourserian } from 'js/lib/user';

import localStorageEx from 'bundles/common/utils/localStorageEx';
import EpicClient from 'bundles/epic/clients/siteMisc';

import type { ColorSchemePreference, ColorSchemeRenderedOnTheClient } from './types';

export const COLOR_THEME_COOKIE = 'colorTheme';
export const DARK_MODE_BANNER_DISMISSED_STORAGE_KEY = 'dark_mode_banner_dismissed';

const getValidColorTheme = (input: string | null | undefined): ColorSchemePreference | undefined => {
  switch (input) {
    case 'light':
      return 'light';
    case 'dark':
      return 'dark';
    case 'system':
      return 'system';
    default:
      return undefined;
  }
};

/**
 * Detects whether the browser supports dark mode.
 * @see https://developer.mozilla.org/en-US/docs/Web/CSS/color_value/light-dark
 * @returns {boolean} true if light-dark() is supported, false otherwise
 */
export const supportsDarkMode = (): boolean => {
  try {
    if (typeof CSS === 'undefined' || !CSS.supports) {
      return false;
    }
    return CSS.supports('color', 'light-dark(red, blue)');
  } catch {
    return false;
  }
};

export const getColorSchemePreference = () => {
  // ignore the cookie for unauthenticated users
  // to reduce the scope of the dark mode project
  if (!isAuthenticatedUser()) return undefined;
  return getValidColorTheme(getCookie(COLOR_THEME_COOKIE));
};

export const setColorSchemePreference = (preference: ColorSchemePreference) => {
  setCookie(COLOR_THEME_COOKIE, preference);
};

export const getThemeFromUrl = (): ColorSchemePreference | undefined => {
  if (typeof window === 'undefined') return undefined;
  const params = new URLSearchParams(window.location.search);
  const themeFromUrl = params.get('theme') ?? params.get('colorTheme') ?? undefined;
  return getValidColorTheme(themeFromUrl);
};
export const enableAppearancePreferences = () => {
  const userId = getUser()?.id;
  const enableForCourserians = isCourserian() && EpicClient.get('allowDarkMode');
  const enableForDogfooding = (EpicClient.get('allowDarkModeByUserId') ?? []).includes(userId);
  const enableForUsersWhoAlreadyHaveTheCookie = Boolean(getCookie(COLOR_THEME_COOKIE));
  const enableWithThemeFromUrl = Boolean(getThemeFromUrl());
  const browserSupportsLightDark = supportsDarkMode();
  return (
    browserSupportsLightDark &&
    (enableForCourserians || enableForDogfooding || enableForUsersWhoAlreadyHaveTheCookie || enableWithThemeFromUrl)
  );
};

export const getInitialColorTheme = () => {
  const themeFromUrl = getThemeFromUrl();
  return themeFromUrl ?? getColorSchemePreference() ?? 'light';
};

export const getColorSchemeRenderedOnTheClient = (
  preference: ColorSchemePreference
): ColorSchemeRenderedOnTheClient => {
  switch (preference) {
    case 'dark':
      return 'dark';
    case 'light':
      return 'light';
    case 'system':
      if (typeof window === 'undefined') return 'light';
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    default:
      return 'light';
  }
};

export const parseLightDarkValue = (value: string) => {
  // This regex matches 'light-dark(<anything>, <anything>)', handling nested commas (e.g. in rgb(a))
  // It uses a balanced approach to match commas not inside parentheses.
  const lightDarkPrefix = 'light-dark(';
  if (!value.startsWith(lightDarkPrefix) || !value.endsWith(')')) return [undefined, undefined];

  const content = value.slice(lightDarkPrefix.length, -1).trim();

  let depth = 0;
  let splitIndex = -1;
  for (let i = 0; i < content.length; i += 1) {
    const char = content[i];
    if (char === '(') {
      depth += 1;
    } else if (char === ')') {
      depth -= 1;
    } else if (char === ',' && depth === 0) {
      splitIndex = i;
      break;
    }
  }
  if (splitIndex === -1) return [undefined, undefined];

  const lightValue = content.slice(0, splitIndex).trim();
  const darkValue = content.slice(splitIndex + 1).trim();

  return [lightValue, darkValue] as const;
};

const getComputedPropertyValue = (propertyName: string) => {
  return getComputedStyle(document.documentElement).getPropertyValue(propertyName);
};

export const getColorCodeFromCssVariable = (token: string, theme: ColorSchemeRenderedOnTheClient): string => {
  const propertyName = token.startsWith('var(') ? token.slice('var('.length, -1) : token;
  const value = getComputedPropertyValue(propertyName).trim();
  if (value.startsWith('light-dark(')) {
    const [lightValue, darkValue] = parseLightDarkValue(value);
    const themeValue = theme === 'dark' ? darkValue : lightValue;
    if (themeValue?.startsWith('var(')) {
      return getColorCodeFromCssVariable(themeValue, theme);
    }
    return themeValue?.trim() ?? '';
  }
  if (value.startsWith('var(')) {
    return getColorCodeFromCssVariable(value, theme);
  }
  return value;
};

export const wasDarkModeBannerDismissedInLocalStorage = (): boolean => {
  return localStorageEx.getItem(DARK_MODE_BANNER_DISMISSED_STORAGE_KEY, JSON.parse, false);
};

export const setDarkModeBannerDismissedInLocalStorage = () => {
  localStorageEx.setItem(DARK_MODE_BANNER_DISMISSED_STORAGE_KEY, true, JSON.stringify);
};
